var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import axios from 'axios';
import { config } from 'lib-shared-backend-config';
import { authManager } from 'utils/auth/authManager';
import { appSearchParamsToURLSearchParams } from 'utils/url/appSearchParams';
import { addInterceptors } from './interceptors/addInterceptors';
/** The axios instance to use. */
const axiosInstance = axios.create();
addInterceptors(axiosInstance);
/** Gets the base query for making api requests with axios. */
export function axiosBaseQuery() {
    return (endpointOptions, _api, extraOptions) => __awaiter(this, void 0, void 0, function* () {
        if (extraOptions === null || extraOptions === void 0 ? void 0 : extraOptions.mockResponse) {
            const { data, delay = 0 } = extraOptions.mockResponse;
            return new Promise((resolve) => setTimeout(() => resolve({ data }), delay));
        }
        const axiosConfig = yield createAxiosConfig(endpointOptions);
        try {
            const _a = yield axiosInstance(axiosConfig), { data } = _a, response = __rest(_a, ["data"]);
            return { data, meta: { response } };
        }
        catch (error) {
            const axiosError = error;
            const { response } = axiosError;
            return {
                error: {
                    status: response === null || response === void 0 ? void 0 : response.status,
                    data: response === null || response === void 0 ? void 0 : response.data,
                },
            };
        }
    });
}
/**
 * Creates an axios request config.
 *
 * @param endpointOptions The endpoint options.
 */
export function createAxiosConfig(endpointOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const { auth, body, contentType, headers, method, params, path, type, version, } = parseEndpointOptions(endpointOptions);
        const baseUrl = config.url[type !== null && type !== void 0 ? type : 'rest'];
        const url = isPaginatedEndpointOptions(endpointOptions)
            ? [path]
            : [version ? `v${yield version}` : undefined, path];
        return Object.assign(Object.assign(Object.assign(Object.assign({ url: [
                baseUrl,
                ...url
                    .filter(Boolean)
                    .map((part) => part.replace(/^\/|\/$/g, '')),
            ].join('/') }, (method ? { method } : {})), (params ? { params: appSearchParamsToURLSearchParams(params) } : {})), (body ? { data: body } : {})), { headers: Object.assign(Object.assign({ ['Content-Type']: contentType !== null && contentType !== void 0 ? contentType : 'application/json' }, ((typeof auth === 'undefined' || auth) && {
                ['Authorization']: yield createAuthorizationHeaderValue(),
            })), headers), validateStatus: (status) => status >= 200 && status < 300 });
    });
}
/** Creates the authorization header value. */
export function createAuthorizationHeaderValue() {
    return __awaiter(this, void 0, void 0, function* () {
        const authData = yield authManager.getAuth();
        const token = authData === null || authData === void 0 ? void 0 : authData.access_token;
        const tokenBase64 = token ? btoa(`token:${token}`) : '';
        return `Basic ${tokenBase64}`;
    });
}
/**
 * Checks whether or not the error is a query error.
 *
 * @param error The error.
 */
export function isQueryError(error) {
    return (error === null || error === void 0 ? void 0 : error.data) !== undefined;
}
/**
 * Checks whether or not the error is a serialized error.
 *
 * @param error The error.
 */
export function isSerializedError(error) {
    return (error === null || error === void 0 ? void 0 : error.stack) !== undefined;
}
/**
 * Checks whether or not the response is with data rather than with error. This
 * format of response is received by dispatched mutations.
 *
 * @param response The rtk-q mutation api response.
 */
export function isResponseWithData(response) {
    return (response.error === undefined);
}
/**
 * Gets an {@link AsyncStatus} from RTK query states.
 *
 * @param states The query states.
 */
export function getAsyncStatusFromRtkQueryStates({ isError, isSuccess, isFetching, isUninitialized, }) {
    if (isUninitialized)
        return 'uninitialized';
    if (isError)
        return 'rejected';
    if (isSuccess && !isFetching)
        return 'fulfilled';
    return 'pending';
}
/**
 * Gets an {@link AsyncStatus} from RTK mutation states.
 *
 * @param states The mutation states.
 */
export function getAsyncStatusFromRtkMutationStates({ isError, isSuccess, isUninitialized, }) {
    if (isUninitialized)
        return 'uninitialized';
    if (isError)
        return 'rejected';
    if (isSuccess)
        return 'fulfilled';
    return 'pending';
}
/**
 * Gets the aggregated async status based on the async status of other async
 * status.
 *
 * @param asyncStatus The list of async status.
 */
export function getAsyncStatus(asyncStatus) {
    if (asyncStatus.some((asyncStatus) => asyncStatus === 'pending')) {
        return 'pending';
    }
    if (asyncStatus.some((asyncStatus) => asyncStatus === 'rejected')) {
        return 'rejected';
    }
    if (asyncStatus.every((asyncStatus) => asyncStatus === 'fulfilled')) {
        return 'fulfilled';
    }
    return 'uninitialized';
}
/**
 * Parses the endpoint options by either returning them as-is or creating them
 * from a string.
 *
 * @param endpointOptions The endpoint options.
 */
function parseEndpointOptions(endpointOptions) {
    return isPaginatedEndpointOptions(endpointOptions)
        ? { path: endpointOptions }
        : endpointOptions;
}
/**
 * Checks whether or not the endpoint options are the paginated variant.
 *
 * @param endpointOptions The endpoint options.
 */
function isPaginatedEndpointOptions(endpointOptions) {
    return typeof endpointOptions === 'string';
}
